<template>
    <section class="h-full">
        <div class="mb-6 " v-if="form.step == 'materialFinishes'">
            <h3 class="text-lg text-center font-medium leading-6 text-gray-900 sm:text-2xl">
                Tell us more about the parts you need to get made
            </h3>
            <br>


            <div class="my-3">
                <ListBoxSelectWithImage
                    labelMain="What type of enclosure or part are you looking to get designed and manufactured?"
                    :options="typeOfEnclosures" v-model="form.designTypeofEnclosure" requires="True" />
            </div>
            <SheetMetal v-if="form.designTypeofEnclosure == 'Sheet Metal'" />
            <CNCMachined v-if="form.designTypeofEnclosure == 'CNC Machined'" />

            <CustomizableDieCastEnc v-if="form.designTypeofEnclosure == 'Customizable Diecast Enclosure'" />
            <ThreeDprinting v-if="form.designTypeofEnclosure == '3D Printing'" />
            <BusBar v-if="form.designTypeofEnclosure == 'Bus Bar'" />
            <Gasket v-if="form.designTypeofEnclosure == 'Gasket'" />
            <div v-if="form.designTypeofEnclosure == 'Other'">

                <BaseTextArea
                    label="Please tell us about your design, and what you require. Our team will assess this information and your design file!"
                    v-model="form.OtherTypeofEnclosure"
                    tooltip="We can source different material as a non-stock request, for additional leadtime and cost." />

            </div>
        </div>
    </section>



    <!-- modal to show missing fields -->
    <!-- Show modal popup when fields are missing -->
    <TransitionRoot as="template" :show="open">
        <Dialog as="div" class="relative z-10" @close="open = false">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
                leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild as="template" enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel
                            class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                            <div>
                                <button @click="open = false" ref="cancelButtonRef" v-if="success == true"
                                    class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                    <CheckIcon class="h-6 w-6 text-green-600" aria-hidden="true" />

                                </button>
                                <button @click="open = false" ref="cancelButtonRef" v-if="success == false"
                                    class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                    <XMarkIcon class="h-6 w-6 text-red-600" aria-hidden="true" />

                                </button>

                                <div class="mt-3 text-center sm:mt-5">
                                    <DialogTitle as="p">
                                        <!-- add missing fields -->
                                        <template v-if="success == false">
                                            <p class="font-semibold">
                                                Please fill out the following fields:

                                            </p>
                                            <p v-for="field in missingFields" :key="field"
                                                class="text-xs items-center text-left leading-6 text-red-900">
                                                * {{ field }}
                                            </p>
                                        </template>
                                        <!-- add success message -->
                                        <template v-if="success == true">
                                            <p>
                                                Thank you for your submission. We will be in touch shortly.
                                            </p>
                                        </template>
                                    </DialogTitle>

                                </div>
                            </div>

                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>


    <section class="my-6 flex justify-between">
        <Button label="Back" class="bg-blue-600 text-white p-2" @click.prevent="form.step = 'start'" />

        <Button label="Next" class="bg-blue-600 text-white p-2" @click.prevent="validate" />

    </section>



</template>

<script setup>

import { ref, inject, reactive } from 'vue'
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue'

import Button from "./formComponents/Button.vue";
import CNCMachined from "./TypeOfEnclosure/CNCMachined.vue";
import SheetMetal from "./TypeOfEnclosure/SheetMetal.vue";
import CustomizableDieCastEnc from "./TypeOfEnclosure/CustomizableDieCastEnc.vue";
import ThreeDprinting from "./TypeOfEnclosure/ThreeDprinting.vue";
import BusBar from "./TypeOfEnclosure/BusBar.vue";
import Gasket from "./TypeOfEnclosure/Gasket.vue";
import { typeOfEnclosures } from "../constants/NewDesignWithCad";
import BaseTextArea from "./formComponents/BaseTextArea.vue";
import ListBoxSelectWithImage from "./formComponents/ListBoxSelectWithImage.vue";
import { CheckIcon, XMarkIcon } from "@heroicons/vue/20/solid";
const form = inject('form')


// check missing fields using watch property of vue js 3 
const missingFields = ref([]);
const success = ref(false);
const open = ref(false);

const validate = () => {
    missingFields.value = [];

    // if design type of enclosure is not selected
    if (!form.designTypeofEnclosure) {
        missingFields.value.push('What type of enclosure or part are you looking to get designed and manufactured? ')
    }


    // missing fields for Sheet Metal data
    if (form.designTypeofEnclosure === 'Sheet Metal') {
        if (!form.typeMaterialSheetMetal) {
            missingFields.value.push('Material Type')
        }
        // only if material type is not selected as other
        if (form.typeMaterialSheetMetal != 'Other' && !form.typeMaterialSheetMetalSize) {
            missingFields.value.push('Material Thickness')
        }
        // if material type is selected as other
        if (form.typeMaterialSheetMetal == 'Other' && !form.typeMaterialSheetMetalOther) {
            missingFields.value.push('Please tell us about the material you require. ')
        }
        if (!form.typeMaterial) {
            missingFields.value.push('Finish Type')
        }
        if (!form.typeMaterialFinishType) {
            missingFields.value.push('Finish Sub Type')
        }
        if (form.typeMaterialFinishType == 'Other' && form.otherPowderCoat == '') {
            missingFields.value.push('Please indicate the powdercoat color you require ')
        }
        if (!form.NewDesignSheetMetalWelding) {
            missingFields.value.push('Does your design require welding?')
        }

        if (!form.DoYouRequireGrpahics) {
            missingFields.value.push('Do you require graphics?')
        }
        if (form.DoYouRequireGrpahics == 'Yes' && !form.reqGraphics) {
            if (!form.reqGraphics) {
                missingFields.value.push('Which type of graphics do you require?')
            }
            if (!form.NewDesignSheetMetalDigitalPrint) {
                missingFields.value.push('Which type of digital print do you require?')
            }

        }
        //  if typeMaterialSheetMetal is selected as "Cold Rolled Steel" then don't check for reqPermanentMarking and NewDesignSheetMetalPermanentMarking
        if (form.typeMaterialSheetMetal != 'Cold Rolled Steel') {
            if (!form.reqPermanentMarking) {
                missingFields.value.push('Does your design require Permanent Marking?')
            }
            if (form.reqPermanentMarking == 'Yes' && !form.NewDesignSheetMetalPermanentMarking) {
                missingFields.value.push('Which permanent marking are you looking for?')
            }
        }

    }

    // missing fields for CNC Machined data
    if (form.designTypeofEnclosure === 'CNC Machined') {
        if (!form.typeMaterialCnc) {
            missingFields.value.push('Material Type')
        }
        // only if material type is not selected as other
        if (form.typeMaterialCnc != 'Other' && !form.typeMaterialCncDependent) {
            missingFields.value.push('Material Thickness')
        }
        // if material type is selected as other
        if (form.typeMaterialCnc == 'Other' && !form.typeMaterialCncOther) {
            missingFields.value.push('Please tell us about the material you require. ')
        }

        // if material type is plastic don't check for finish type and finish sub type
        if (form.typeMaterialCnc != 'Plastic') {
            if (!form.typeMaterialFinishCNC) {
                missingFields.value.push('Finish Type')
            }
            if (!form.typeMaterialFinishType) {
                missingFields.value.push('Finish Sub Type')
            }
        }

    }

    // missing fields for Customizable Die Cast Enclosure data

    if (form.designTypeofEnclosure === 'Customizable Diecast Enclosure') {
        if (!form.typeDiecast) {
            missingFields.value.push('Material Type')
        }
        // only if diecast type is not selected as other
        if (form.typeDiecast != 'Other' && !form.typeDiecastSubSelection) {
            missingFields.value.push('Select sub type')
        }

        // if diecast type is selected as other
        if (form.typeDiecast == 'Other' && !form.typeDiecastOther) {
            missingFields.value.push('Please tell us about the material you require. ')
        }

        if (!form.typeMaterialFinish) {
            missingFields.value.push('Finish Type')
        }
        if (!form.finishType) {
            missingFields.value.push('Select sub type of Finish Type')
        }
        //  form.reqGraphics
        if (!form.reqGraphics) {
            missingFields.value.push('Do you require graphics?')
        }
        // if reqGraphics is yes
        if (form.reqGraphics == 'Yes' && !form.reqGraphicsType) {
            if (!form.reqGraphicsType) {
                missingFields.value.push('Which type of digital print do you require? ')
            }
            // digital print
            if (!form.DigitalPrint) {
                missingFields.value.push('Do you require digital print?')
            }

        }

        if (!form.reqPermanentMarking) {
            missingFields.value.push('Does your design require Permanent Marking?')
        }
        // if reqPermanentMarking is yes
        if (form.reqPermanentMarking == 'Yes' && !form.PermanentMarking) {
            missingFields.value.push('Which permanent marking are you looking for?')
        }

    }

    // missing fields for Bus Bar data
    if (form.designTypeofEnclosure === 'Bus Bar') {
        if (!form.typeBusBarMaterial) {
            missingFields.value.push('Material Type')
        }
        // only if material type is not selected as other
        if (form.typeBusBarMaterial != 'Other' && !form.materialThickness) {
            missingFields.value.push('Material Thickness')
        }
        // if material type is selected as other
        if (form.typeBusBarMaterial == 'Other' && !form.typeBusBarMaterialOther) {
            missingFields.value.push('Please tell us about the material you require. ')
        }

        if (!form.bareMetal) {
            missingFields.value.push('Bare-Metal Finish')
        }
        // tinPlating only if typeBusBarMaterial is Copper
        if (form.typeBusBarMaterial === 'Copper' && !form.tinPlating) {
            missingFields.value.push('Do you require Tin Plating ?')
        }

    }

    // missing fields for 3D Printing data
    if (form.designTypeofEnclosure === '3D Printing') {
        if (!form.type3Dprinting) {
            missingFields.value.push('Material Type')
        }
        // if material type is selected as other
        if (form.type3Dprinting == 'Other' && !form.type3DprintingOther) {
            missingFields.value.push('Please tell us about the material you require. ')
        }

        if (!form.colorFilament) {
            missingFields.value.push('What color filament do you require?')
        }
    }

    // missing fields for gasket
    if (form.designTypeofEnclosure === 'Gasket') {
        if (!form.typeGasketMaterial) {
            missingFields.value.push('Material Type')
        }
        // if material type is selected as other
        if (form.typeGasketMaterial == 'Other' && !form.typeGasketMaterialOther) {
            missingFields.value.push('Please tell us about the material you require. ')
        }

    }



    if (missingFields.value.length == 0) {
        form.step = 'projectTimeline';
    } else {
        success.value = false;
        open.value = true;
    }



}

</script>